import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Mail,
  MapPin,
  Phone,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Heart,
} from "lucide-react";

const SERVICES = [
  { name: "Branding & Identity", href: "/services#branding" },
  { name: "Digital Marketing", href: "/services#marketing" },
  { name: "Web & App Development", href: "/services#development" },
  { name: "UI/UX Design", href: "/services#design" },
  { name: "AI Automation", href: "/services#ai" },
  { name: "Content Creation", href: "/services#content" },
];

const COMPANY = [
  { name: "About Us", href: "/about" },
  { name: "Our Work", href: "/transformations" },
  { name: "Platform", href: "/platform" },
  { name: "Pricing", href: "/pricing" },
  { name: "BusiMag", href: "/busimag" },
  { name: "Studio", href: "/studio" },
  { name: "Contact", href: "/contact" },
];

const SOCIALS = [
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Youtube, href: "#", label: "YouTube" },
];

export default function Footer() {
  return (
    <footer className="relative overflow-hidden">
      {/* Soft gradient top border */}
      <div className="h-px bg-gradient-to-r from-transparent via-violet-200 to-transparent" />

      {/* Main Footer */}
      <div className="bg-violet-tint">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand Col */}
            <div className="sm:col-span-2 lg:col-span-1">
              <Link to="/" className="inline-block mb-5">
                <img
                  src="/assets/images/Busigrow_Registered-1751003166599.png"
                  alt="BusiGrow"
                  className="h-10 w-auto object-contain"
                />
              </Link>
              <p className="text-slate-500 text-sm leading-relaxed max-w-xs mb-6">
                Empowering businesses with branding, marketing, development & AI
                — everything you need to grow, under one roof.
              </p>
              <div className="flex items-center gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    className="p-2.5 rounded-xl glass hover:bg-violet-100 hover:scale-110 active:scale-95 transition-all duration-300 group"
                  >
                    <s.icon className="w-4 h-4 text-slate-400 group-hover:text-violet-600 transition-colors" />
                  </a>
                ))}
              </div>
            </div>

            {/* Services Col */}
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-4 tracking-wide uppercase">
                Services
              </h4>
              <ul className="space-y-3">
                {SERVICES.map((s) => (
                  <li key={s.name}>
                    <Link
                      to={s.href}
                      className="text-sm text-slate-500 hover:text-violet-600 hover:translate-x-1 inline-block transition-all duration-200"
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Col */}
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-4 tracking-wide uppercase">
                Company
              </h4>
              <ul className="space-y-3">
                {COMPANY.map((c) => (
                  <li key={c.name}>
                    <Link
                      to={c.href}
                      className="text-sm text-slate-500 hover:text-violet-600 hover:translate-x-1 inline-block transition-all duration-200"
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact & CTA Col */}
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-4 tracking-wide uppercase">
                Get in Touch
              </h4>
              <div className="space-y-3 mb-6">
                <a href="mailto:hello@busigrow.com" className="flex items-center gap-2.5 text-sm text-slate-500 hover:text-violet-600 transition-colors">
                  <Mail className="w-4 h-4" />
                  hello@busigrow.com
                </a>
                <div className="flex items-center gap-2.5 text-sm text-slate-500">
                  <MapPin className="w-4 h-4 shrink-0" />
                  India &middot; Middle East &middot; SEA
                </div>
              </div>
              <Link
                to="/contact"
                className="btn-shine inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold rounded-xl shadow-apple hover:shadow-apple-lg hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
              >
                Start Your Project
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-200/60">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 sm:py-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <p className="text-xs text-slate-400 flex items-center gap-1">
              &copy; {new Date().getFullYear()} BusiGrow. Made with
              <Heart className="w-3 h-3 text-red-400 fill-red-400 inline" />
              in India.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-xs text-slate-400 hover:text-violet-600 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-xs text-slate-400 hover:text-violet-600 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
